import axios from 'axios';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useCampaign,
  useInstagramConnect,
  useIsProviderConnected,
  useDisconnectSocialProvider,
} from '@/api';
import { ConnectSocialPage } from '@/components';
import { redirectToOAuth } from '@/utils/oauth';
import { SocialProvider } from '@/api/queries/settings/constants';

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? error.message;
  }
  return error instanceof Error ? error.message : '';
};

/**
 * Connect-Instagram gate in the creator campaign flow. Reached from the tasks
 * hub's "Next" when Instagram isn't connected; the creator must connect before
 * continuing. Reuses ConnectSocialPage with campaign-flow navigation.
 */
export function CampaignConnectInstagram() {
  const { id } = useParams({ from: '/_authenticated/campaign/$id/connect' });
  const navigate = useNavigate();

  const { data: campaign } = useCampaign(id);
  const { isConnected, username, isLoading } = useIsProviderConnected(SocialProvider.INSTAGRAM);
  const {
    mutateAsync: connect,
    isPending: isConnecting,
    error: connectError,
  } = useInstagramConnect();
  const {
    mutateAsync: disconnect,
    isPending: isDisconnecting,
    error: disconnectError,
  } = useDisconnectSocialProvider();

  const handleConnect = async () => {
    try {
      const { url } = await connect();
      redirectToOAuth(url, SocialProvider.INSTAGRAM);
    } catch {
      // Failure is surfaced to the user via `connectError` below; catch here so a
      // network error or missing authorization URL doesn't become an unhandled
      // promise rejection.
    }
  };

  return (
    <ConnectSocialPage
      providerName="Instagram"
      icon="instagram"
      isConnected={isConnected}
      username={username}
      isLoadingConnection={isLoading}
      isConnecting={isConnecting}
      isDisconnecting={isDisconnecting}
      error={getErrorMessage(connectError) || getErrorMessage(disconnectError)}
      onConnect={handleConnect}
      onDisconnect={() => disconnect(SocialProvider.INSTAGRAM)}
      onBack={() => navigate({ to: '/campaign/$id/tasks', params: { id } })}
      backLabel={campaign?.name ?? 'Your campaign'}
      // The Confirm Address / Claim step is delivered separately; continue to the
      // dashboard for now once connected.
      onContinue={() => navigate({ to: '/dashboard' })}
      continueLabel="Continue"
      requireConnection
    />
  );
}

export default CampaignConnectInstagram;
